import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { useToast } from '@/hooks/useToast';
// TODO: Implement transferCampaignToGoogleAdsAccount in backend
// import { transferCampaignToGoogleAdsAccount } from '@/lib/apis';
import { campaignDataAtom } from '@/store';

// Temporary stub until backend implementation is ready
const transferCampaignToGoogleAdsAccount = async (_payload: TransferToGoogleAdsPayload) => {
  throw new Error('Campaign transfer to Google Ads account is not yet implemented');
};

interface TransferToGoogleAdsPayload {
  campaignId: number;
  targetGoogleAdsAccountId: string;
}

export function useCampaignGoogleAdsTransfer() {
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<{
    id: number;
    name: string;
    currentGoogleAdsAccountId: string;
  } | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setCampaignData = useSetAtom(campaignDataAtom);
  const { toast } = useToast();

  const transferMutation = useMutation({
    mutationFn: async (payload: TransferToGoogleAdsPayload) => {
      return transferCampaignToGoogleAdsAccount(payload);
    },
    onSuccess: (_, variables) => {
      // Invalidate campaigns query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });

      // Invalidate specific campaign query if it exists
      queryClient.invalidateQueries({ queryKey: ['campaign', variables.campaignId] });

      toast({
        title: 'Transfer Successful',
        description: `Campaign has been transferred to the new Google Ads account successfully.`,
      });

      setIsTransferModalOpen(false);
      setSelectedCampaign(null);

      // Clear the selected campaign to prevent navigation to transferred campaign
      setCampaignData(null);

      // Navigate to campaigns page after successful transfer
      console.log('navigate to campaigns');
      navigate({ to: '/campaigns' });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to transfer campaign';

      toast({
        title: 'Transfer Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const openTransferModal = (campaignId: number, campaignName: string, currentGoogleAdsAccountId: number) => {
    setSelectedCampaign({
      id: campaignId,
      name: campaignName,
      currentGoogleAdsAccountId: currentGoogleAdsAccountId.toString(),
    });
    setIsTransferModalOpen(true);
  };

  const closeTransferModal = () => {
    setIsTransferModalOpen(false);
    setSelectedCampaign(null);
  };

  const handleTransfer = (targetGoogleAdsAccountId: string) => {
    if (!selectedCampaign) {
      toast({
        title: 'No Campaign Selected',
        description: 'Please select a campaign to transfer.',
        variant: 'destructive',
      });
      return;
    }

    transferMutation.mutate({
      campaignId: selectedCampaign.id,
      targetGoogleAdsAccountId,
    });
  };

  return {
    isTransferModalOpen,
    selectedCampaign,
    openTransferModal,
    closeTransferModal,
    handleTransfer,
    isTransferring: transferMutation.isPending,
  };
}
